import { useState, useEffect } from "react"
import {
  CardContent,
  IconButton,
  Typography,
  Container,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material"
import {
  CollectionsOutlined,
  Close,
  ArrowBackIos,
  ArrowForwardIos
} from "@mui/icons-material"
import { ThemeProvider } from "@mui/material/styles"
import axios from "axios"

// Import logos
import LogoMobile from "../../assets/fav.png"
import Logo from "../../assets/amal-logo-navy.png"

// Import theme & styled components
import {
  theme,
  PageWrapper,
  LogoContainer,
  DesktopLogo,
  MobileLogo,
  InfoCard,
  HeaderTitle,
  DescriptionText,
  StatContainer,
  StatBox,
  StatValue,
  StatLabel,
  SwatchesGrid,
  SwatchCard,
  SwatchTitle,
  SwatchMediaContainer,
  SwatchMedia,
  GalleryIconButton,
  GalleryButtonContainer,
  CopyrightContainer,
  CopyrightText,
  DisclaimerText,
  ModalImageContainer,
  ModalImage,
  CarouselPrevButton,
  CarouselNextButton,
  CenterMessageContainer,
} from "./styles"

export default function SentSwatchesPage() {
  const [collectionDialog, setCollectionDialog] = useState(false)
  
  // State for API integration
  const [swatches, setSwatches] = useState([])
  const [collectionInfo, setCollectionInfo] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedSwatchImages, setSelectedSwatchImages] = useState([])
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  // Read the parameters for this specific sent-swatches link.
  const urlParams = new URLSearchParams(window.location.search)
  const cuc = urlParams.get("cuc") || ""
  const cun = urlParams.get("cun") || ""
  const un = urlParams.get("un") || ""
  const urlToken = urlParams.get("token") || ""

  useEffect(() => {
    let cancelled = false

    const loadSwatches = async () => {
      // Reset page state immediately so data from a previous client is never shown.
      setLoading(true)
      setError(null)
      setSwatches([])
      setCollectionInfo({})

      const username = un || cun || cuc
      if (!urlToken && !username) {
        setError("Error loading swatches parameters.")
        setLoading(false)
        return
      }

      try {
        let authToken = urlToken

        if (!authToken) {
          // Tokens must be cached per username. A single shared token can show
          // the previous client's data when a different sent link is opened.
          const storageKey = `amal_auth_token_${username}`
          authToken = localStorage.getItem(storageKey)

          if (!authToken) {
            const authResponse = await axios.post(
              "https://amal.platform.simplifii.com/api/v1/admin/authenticate",
              { username, password: username },
              { headers: { "Content-Type": "application/json" } }
            )

            if (authResponse.data.status !== 1 || !authResponse.data.token) {
              throw new Error("Authentication failed")
            }

            authToken = authResponse.data.token
            localStorage.setItem(storageKey, authToken)
          }
        }

        if (cancelled) return

        const params = new URLSearchParams()
        params.set("cuc", cuc)
        params.set("cun", cun)
        params.set("token", authToken)

        const response = await axios.get(
          `https://amal.platform.simplifii.com/api/v1/custom/LoanedSwatches?${params.toString()}`
        )

        if (cancelled) return

        const fetchedSwatches =
          response.data.response?.data ||
          response.data.response ||
          response.data.data ||
          []

        setSwatches(Array.isArray(fetchedSwatches) ? fetchedSwatches : [])
        setCollectionInfo({
          client: response.data.client || "Client",
          swatches_count: response.data.swatches_count || fetchedSwatches.length || 0,
          collection_description: response.data.collection_description || ""
        })
        setLoading(false)
      } catch (err) {
        if (cancelled) return
        console.error("Error loading sent swatches:", err)
        setError(err.message === "Authentication failed"
          ? "Authentication failed"
          : "Failed to load swatches")
        setLoading(false)
      }
    }

    loadSwatches()

    return () => {
      // Ignore late responses if the URL/client changes before the request ends.
      cancelled = true
    }
  }, [cuc, cun, un, urlToken])

  const handleViewAllImages = (swatch) => {
    const additionalImages = (swatch.additional_images || []).slice(0, 2)
    const allImages = [
      { image: swatch.primary_image },
      ...additionalImages
    ]
    setSelectedSwatchImages(allImages)
    setSelectedImageIndex(0)
    setCollectionDialog(true)
  }

  const handleCarouselNext = () => {
    setSelectedImageIndex((prev) => (prev + 1) % selectedSwatchImages.length)
  }

  const handleCarouselPrev = () => {
    setSelectedImageIndex((prev) => (prev - 1 + selectedSwatchImages.length) % selectedSwatchImages.length)
  }

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CenterMessageContainer>
          <Typography variant="h6">Loading swatches...</Typography>
        </CenterMessageContainer>
      </ThemeProvider>
    )
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <CenterMessageContainer>
          <Typography variant="h6" color="error">{error}</Typography>
        </CenterMessageContainer>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <PageWrapper>
        <Container sx={{ py: 4 }}>
          {/* Logo Section */}
          <LogoContainer>
            <DesktopLogo src={Logo} alt="Amal Logo" />
            <MobileLogo src={LogoMobile} alt="Amal Logo Mobile" />
          </LogoContainer>

          {/* Collection / Sent Swatches Info Card */}
          <InfoCard>
            <CardContent sx={{ p: 2.5 }}>
              <HeaderTitle variant="h5" component="h1">
                Welcome, {collectionInfo.client}
              </HeaderTitle>
              
              {collectionInfo.collection_description && (
                <DescriptionText variant="body1">
                  {collectionInfo.collection_description}
                </DescriptionText>
              )}

              <StatContainer>
                <StatBox>
                  <StatValue variant="h4">
                    {collectionInfo.swatches_count}
                  </StatValue>
                  <StatLabel variant="body2">
                    Total Swatches
                  </StatLabel>
                </StatBox>
              </StatContainer>
            </CardContent>
          </InfoCard>

          <SwatchesGrid>
            {swatches.map((swatch, index) => (
              <Fade in timeout={600 + index * 100} key={swatch.swatch_unique_code || swatch.unique_code || index}>
                <SwatchCard>
                  <CardContent sx={{ p: 2.5, pb: 1.5 }}>
                    <SwatchTitle variant="h6" component="h2">
                      {swatch.swatch_unique_code || swatch.unique_code}
                    </SwatchTitle>
                  </CardContent>

                  <SwatchMediaContainer>
                    <SwatchMedia
                      component="img"
                      height={400}
                      image={(swatch.primary_image_thumbnail || swatch.primary_image || '').replace('w_330', 'w_400')}
                      alt={swatch.swatch_unique_code || swatch.unique_code}
                    />

                    {swatch.additional_images && swatch.additional_images.length > 0 && (
                      <GalleryButtonContainer>
                        <GalleryIconButton onClick={() => handleViewAllImages(swatch)}>
                          <CollectionsOutlined fontSize="small" />
                        </GalleryIconButton>
                      </GalleryButtonContainer>
                    )}
                  </SwatchMediaContainer>
                </SwatchCard>
              </Fade>
            ))}
          </SwatchesGrid>

          {/* Copyright Section */}
          <CopyrightContainer>
            <CopyrightText variant="body2">
              © 2026 Amal Embroideries Pvt
            </CopyrightText>
            <DisclaimerText variant="caption">
              All images on this website are owned by Amal Embroideries Pvt and are protected by copyright law. No image may be copied, downloaded, reproduced, or used without prior written permission.
            </DisclaimerText>
          </CopyrightContainer>
        </Container>

        {/* Additional Images Modal */}
        <Dialog open={collectionDialog} onClose={() => setCollectionDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">All Images</Typography>
            <IconButton onClick={() => setCollectionDialog(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <ModalImageContainer>
              {selectedSwatchImages.length > 0 && (
                <ModalImage
                  src={(selectedSwatchImages[selectedImageIndex]?.image || '').replace('w_330', 'w_1100')}
                  alt={`Additional image ${selectedImageIndex + 1}`}
                />
              )}
              {selectedSwatchImages.length > 1 && (
                <>
                  <CarouselPrevButton onClick={handleCarouselPrev}>
                    <ArrowBackIos />
                  </CarouselPrevButton>
                  <CarouselNextButton onClick={handleCarouselNext}>
                    <ArrowForwardIos />
                  </CarouselNextButton>
                  <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                    {selectedImageIndex + 1} of {selectedSwatchImages.length}
                  </Typography>
                </>
              )}
            </ModalImageContainer>
          </DialogContent>
        </Dialog>
      </PageWrapper>
    </ThemeProvider>
  )
}
